import BubbleCommon, {
  influencerStreamingInfluencerTipsText,
  influencerPartneredTipsText,
  influencerSumTipsText,
  influencerVideoTipsText,
  influencerBrandListSumTipsText,
} from '@op/components/BubbleCommon';
import CardLoading from '@op/components/CardLoading';
import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import HoverSvgIcon from '@op/components/HoverSvgIcon';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { EIGHTBALL_EDITION_ID } from '@op/pages/community/video/components/utils';
import { gameInfoType } from '@op/types/common';
import { DBO_TM } from '@op/utils/format';
import React, { useMemo, useState } from 'react';
import { Segment } from 'tea-component';
import { useIntl } from 'umi';

export default function CommonFilter({
  gameInfo,
  children,
  isBrandList = false,
  defaultMediaType = 'sum',
}: {
  gameInfo: gameInfoType;
  children: (params: any) => JSX.Element;
  isBrandList?: boolean;
  defaultMediaType?: string;
}) {
  const { formatMessage } = useIntl();
  const [mediaType, setMediaType] = useState(defaultMediaType);
  const [influencerType, setInfluencerType] = useState('sum');
  const { loading, changeFilter, filter, filterOptions } =
    useFilterDataHook({
      pageName: 'Influencer',
      hasKolTags: true,
      gameInfo,
    });

  // 8 Ball Pool 专属：添加黑灰产网红筛选器
  const finalFilterOptions = useMemo(() => {
    if (gameInfo?.edition_unified_id === EIGHTBALL_EDITION_ID) {
      return [...filterOptions, 'blackGrayAuthor' as any];
    }
    return filterOptions;
  }, [filterOptions, gameInfo, isBrandList]);

  const requestGameInfo = useMemo(() => {
    const { filterInstanceId, ...restGameInfo } = gameInfo as any;
    return restGameInfo;
  }, [gameInfo]);

  const params = useMemo(() => {
    const obj: any = {
      ...filter,
      media_types: [mediaType],
      kol_type: influencerType,
    };
    return obj;
  }, [mediaType, filter, influencerType]);

  const mediaTypeList = [
    {
      text: (
        <div className="dbo-inlineflex dbo-segment-tipsbox">
          <span>
            {formatMessage({
              id: isBrandList ? 'Non-streaming Influencers' : 'Sum Influencers',
            })}
          </span>
          <BubbleCommon
            placement="top"
            tipsMap={
              isBrandList
                ? influencerBrandListSumTipsText
                : influencerSumTipsText
            }
            customIcon={
              <HoverSvgIcon selected={mediaType === 'sum'}></HoverSvgIcon>
            }
          ></BubbleCommon>
        </div>
      ),
      value: 'sum',
    },
    {
      text: (
        <div className="dbo-inlineflex dbo-segment-tipsbox">
          <span>{formatMessage({ id: 'Video Influencers' })}</span>
          <BubbleCommon
            placement="top"
            showIcon={false}
            tipsMap={influencerVideoTipsText}
          ></BubbleCommon>
        </div>
      ),
      value: 'video',
    },
    {
      text: formatMessage({ id: 'Post Influencers' }),
      value: 'post',
    },
    {
      text: (
        <div className="dbo-inlineflex dbo-segment-tipsbox">
          <BubbleCommon
            placement="top"
            tipsMap={influencerStreamingInfluencerTipsText}
            showIcon={false}
          >
            <span style={{ cursor: 'pointer' }}>
              {formatMessage({ id: 'Streaming Influencers' })}
            </span>
          </BubbleCommon>
        </div>
      ),
      value: 'stream',
    },
  ];

  const influencerGenreList = [
    {
      text: formatMessage({ id: 'Total' }),
      value: 'sum',
    },
    {
      text: (
        <div className="dbo-inlineflex dbo-segment-tipsbox">
          <span>{formatMessage({ id: 'Partnered' })}</span>
          <BubbleCommon
            placement="top"
            tipsMap={influencerPartneredTipsText}
            customIcon={
              <HoverSvgIcon
                selected={influencerType === 'partnered'}
              ></HoverSvgIcon>
            }
          ></BubbleCommon>
        </div>
      ),
      value: 'partnered',
    },
    {
      text: formatMessage({ id: 'Organic' }),
      value: 'organic',
    },
  ];

  // const deleteFilter: Array<FilterKeys> = useMemo(() => {
  //   if (
  //     (!isBrandList && !['sum', 'stream'].includes(mediaType)) ||
  //     (isBrandList && mediaType !== 'stream')
  //   ) {
  //     return ['kolTags'];
  //   }

  //   return [];
  // }, [mediaType]);

  const InfluencerTypeElement = (
    <Segment
      className="dbo-influencer-segment segment-light"
      value={influencerType}
      onChange={(value) => {
        setInfluencerType(value);
        buriedBtnPoint('IBT_TAB1', value);
      }}
      options={influencerGenreList}
    />
  );

  return (
    <div className="common-filter-wrapper">
      <FilterFixed
        filter={finalFilterOptions}
        // renderExcludeFilter={deleteFilter}
        operateShowOptions={['history', 'save', 'more']}
        defaultConfig={{
          channel: {
            channelKey: 'social',
            isSpecialChannel: true,
          },
          date: {
            dateTypeList: ['date'],
          },
          regionAndLanguage: {
            regionTipsKey: 'influencer',
            languageTipsKey: 'influencer',
            regionGolobalTipsKey: 'influencer',
          },
          group: { isBrandPage: true },
          region: {
            needExcludeRegion: true
          }
        }}
        dealwithFilterOptionsMap={{
          channel: (options) => ({
            ...(options || {}),
            social: options.social.filter((item: string) => item !== 'reddit'),
          }),
        }}
        historyConfig={{ fixFilter: { date_type: 'daily' } }}
        defaultValue={DBO_TM.utcFilterRangeDay(6, 0)}
        finallyDealwithDefaultValue={(value) => {
          const { date_type, ...otherProps } = value;
          return { ...otherProps, date_type: 'daily' };
        }}
        confirm={(value?: any) => {
          const { channel, channel_type, language, wo_china, region, ...otherValue } = value || {};
          changeFilter({
            ...requestGameInfo,
            ...otherValue,
            channels: channel.length === 0 ? [] : channel,
            languages: language,
            region: wo_china ? [] : region,
            exclude_region: wo_china ? ['chn'] : []
          });
        }}
      ></FilterFixed>
      <CardLoading loading={loading}>
        <Segment
          style={{ marginRight: 10 }}
          className="dbo-influencer-segment segment-light"
          value={mediaType}
          onChange={(value) => {
            setMediaType(value);
            buriedBtnPoint('IBT_TAB2', value);
          }}
          options={mediaTypeList}
        />
        {InfluencerTypeElement}
        {children({
          params,
          mediaType,
          hasKolTagsFilter: filterOptions.includes('kolTags'),
        })}
      </CardLoading>
    </div>
  );
}
